// src/components/management/Properties.jsx

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import "../../styles/components/management/Properties.css";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function PropertyManager() {
  // — State —
  const [properties, setProperties]       = useState([]);
  const [units, setUnits]                 = useState([]);
  const [users, setUsers]                 = useState([]);
  const [assignments, setAssignments]     = useState([]);

  const [newProperty, setNewProperty]     = useState({
    id: null,
    name: "",
    address: "",
    post_code: "",
    city: "",
    trade_code: "",
    beds24_api_key: "",
    notes: "",
    image: null,
  });
  const [selectedImage, setSelectedImage] = useState(null);

  const [newUnit, setNewUnit] = useState({
    id: null,
    name: "",
    access_code: "",
    property_id: "",
    beds24_id: "",
    ical_link: "",
  });

  const [assignUserId, setAssignUserId]         = useState("");
  const [assignPropertyId, setAssignPropertyId] = useState("");

  const { user } = useAuth();

  // — Audit logger —
  const logAudit = useCallback(async (action, details) => {
    await fetch(`${API_BASE}/app/API/rbmgt/AuditLogs.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id:   user?.id || 0,
        user_name: `${user?.name || "System"} ${user?.surname || ""}`.trim(),
        action,
        details,
      }),
    });
  }, [user]);

  // — Fetch all data —
  const fetchAll = useCallback(async () => {
    try {
      const [pRes, uRes, usrRes, asgRes] = await Promise.all([
        fetch(`${API_BASE}/app/API/rbmgt/property.php`),
        fetch(`${API_BASE}/app/API/rbmgt/unit.php`),
        fetch(`${API_BASE}/app/API/rbmgt/add_user.php`),
        fetch(`${API_BASE}/app/API/rbmgt/user_property.php`),
      ]);
      const [pData, uData, usrData, asgData] = await Promise.all([
        pRes.json(), uRes.json(), usrRes.json(), asgRes.json(),
      ]);

      if (pData.success) setProperties(pData.properties);
      if (uData.success) setUnits(uData.units);
      if (usrData.success) setUsers(usrData.users);
      if (asgData.success) setAssignments(asgData.assignments);

      await logAudit("Fetch Data", "Loaded properties, units, users & assignments");
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [logAudit]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // — Handlers for Properties —
  const handlePropertyImageChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result);
      setNewProperty(np => ({ ...np, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handlePropertySubmit = async e => {
    e.preventDefault();
    const method = newProperty.id ? "PUT" : "POST";
    await fetch(`${API_BASE}/app/API/rbmgt/property.php`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newProperty),
    });
    await logAudit(
      method === "POST" ? "Create Property" : "Edit Property",
      newProperty.name
    );
    setNewProperty({
      id: null,
      name: "", address: "", post_code: "", city: "",
      trade_code: "", beds24_api_key: "", notes: "", image: null,
    });
    setSelectedImage(null);
    fetchAll();
  };

  const handlePropertyDelete = async id => {
    if (!window.confirm("Delete this property?")) return;
    await fetch(`${API_BASE}/app/API/rbmgt/property.php?id=${id}`, { method: "DELETE" });
    await logAudit("Delete Property", `ID: ${id}`);
    fetchAll();
  };

  // — Handlers for Units —
  const handleUnitSubmit = async e => {
    e.preventDefault();
    const method = newUnit.id ? "PUT" : "POST";
    await fetch(`${API_BASE}/app/API/rbmgt/unit.php`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newUnit),
    });
    await logAudit(method === "POST" ? "Create Unit" : "Edit Unit", newUnit.name);
    setNewUnit({ id: null, name: "", access_code: "", property_id: "", beds24_id: "", ical_link: "" });
    fetchAll();
  };

  const handleUnitDelete = async id => {
    if (!window.confirm("Delete this unit?")) return;
    await fetch(`${API_BASE}/app/API/rbmgt/unit.php?id=${id}`, { method: "DELETE" });
    await logAudit("Delete Unit", `ID: ${id}`);
    fetchAll();
  };

  // — Handlers for Assignments —
  const handleAssignProperty = async () => {
    if (!assignUserId || !assignPropertyId) return;
    await fetch(`${API_BASE}/app/API/rbmgt/user_property.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: assignUserId, property_id: assignPropertyId }),
    });
    await logAudit("Assign Property", `User ${assignUserId} → Property ${assignPropertyId}`);
    setAssignPropertyId("");
    fetchAll();
  };

  const handleUnassignProperty = async (userId, propertyId) => {
    if (!window.confirm("Unassign this property from the user?")) return;
    await fetch(
      `${API_BASE}/app/API/rbmgt/user_property.php?user_id=${userId}&property_id=${propertyId}`,
      { method: "DELETE" }
    );
    await logAudit("Unassign Property", `User ${userId} × Property ${propertyId}`);
    fetchAll();
  };

  return (
    <div className="property-manager">

      {/* Property Form */}
      <h2>Property Management</h2>
      <form className="property-form" onSubmit={handlePropertySubmit} autoComplete="on">
        <input name="name" placeholder="Property Name" value={newProperty.name}
          onChange={e => setNewProperty(np => ({ ...np, name: e.target.value }))} required />
        <input name="address" placeholder="Address" value={newProperty.address}
          onChange={e => setNewProperty(np => ({ ...np, address: e.target.value }))} required />
        <input name="post_code" placeholder="Post Code" value={newProperty.post_code}
          onChange={e => setNewProperty(np => ({ ...np, post_code: e.target.value }))} />
        <input name="city" placeholder="City" value={newProperty.city}
          onChange={e => setNewProperty(np => ({ ...np, city: e.target.value }))} />
        <input name="trade_code" placeholder="Trade Code" value={newProperty.trade_code}
          onChange={e => setNewProperty(np => ({ ...np, trade_code: e.target.value }))} />
        <input name="beds24_api_key" placeholder="Beds24 API Key" value={newProperty.beds24_api_key}
          onChange={e => setNewProperty(np => ({ ...np, beds24_api_key: e.target.value }))} />
        <textarea name="notes" placeholder="Notes" value={newProperty.notes}
          onChange={e => setNewProperty(np => ({ ...np, notes: e.target.value }))} />
        <input type="file" accept="image/*" onChange={handlePropertyImageChange} />
        {selectedImage && <img src={selectedImage} alt="Preview" style={{ width: 120, marginTop: 10 }} />}
        <button type="submit">{newProperty.id ? "Update Property" : "Create Property"}</button>
      </form>

      {/* Properties Table */}
      <h2>Properties</h2>
      <div className="table-responsive">
        <table className="property-table">
          <thead>
            <tr>
              <th>Preview</th>
              <th>Name</th>
              <th>Address</th>
              <th>Post Code</th>
              <th>City</th>
              <th>Trade Code</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map(p => (
              <tr key={p.PROPERTY_ID}>
                <td>{p.PROPERTY_IMAGE ? <img src={p.PROPERTY_IMAGE} alt="" style={{ width: 80 }} /> : "—"}</td>
                <td>{p.PROPERTY_NAME}</td>
                <td>{p.PROPERTY_ADDRESS}</td>
                <td>{p.PROPERTY_POST_CODE}</td>
                <td>{p.PROPERTY_CITY}</td>
                <td>{p.PROPERTY_TRADE_CODE}</td>
                <td>{p.NOTES}</td>
                <td>
                  <button onClick={() => setNewProperty({
                    id: p.PROPERTY_ID,
                    name: p.PROPERTY_NAME,
                    address: p.PROPERTY_ADDRESS,
                    post_code: p.PROPERTY_POST_CODE,
                    city: p.PROPERTY_CITY,
                    trade_code: p.PROPERTY_TRADE_CODE,
                    beds24_api_key: p.PROPERTY_BEDS24_API_KEY,
                    notes: p.NOTES,
                    image: p.PROPERTY_IMAGE,
                  })}>Edit</button>
                  <button onClick={() => handlePropertyDelete(p.PROPERTY_ID)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Unit Form */}
      <h2>Unit Management</h2>
      <form className="unit-form" onSubmit={handleUnitSubmit} autoComplete="on">
        <select required
          value={newUnit.property_id}
          onChange={e => setNewUnit(nu => ({ ...nu, property_id: e.target.value }))}
        >
          <option value="">Select Property</option>
          {properties.map(p => (
            <option key={p.PROPERTY_ID} value={p.PROPERTY_ID}>{p.PROPERTY_NAME}</option>
          ))}
        </select>

        <input name="unit_name" placeholder="Unit Name" value={newUnit.name}
          onChange={e => setNewUnit(nu => ({ ...nu, name: e.target.value }))} required />

        <input name="access_code" placeholder="Access Code" value={newUnit.access_code}
          onChange={e => setNewUnit(nu => ({ ...nu, access_code: e.target.value }))} />

        <input name="beds24_id" placeholder="Beds24 Unit ID" value={newUnit.beds24_id}
          onChange={e => setNewUnit(nu => ({ ...nu, beds24_id: e.target.value }))} />

        <input name="ical_link" type="url" placeholder="iCal Link (URL)" value={newUnit.ical_link}
          onChange={e => setNewUnit(nu => ({ ...nu, ical_link: e.target.value }))} />

        <button type="submit">{newUnit.id ? "Update Unit" : "Create Unit"}</button>
      </form>

      {/* Units Table */}
      <div className="table-responsive">
        <table className="unit-table">
          <thead>
            <tr>
              <th>Unit</th>
              <th>Property</th>
              <th>Access Code</th>
              <th>Beds24 ID</th>
              <th>iCal Link</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {units.map(u => (
              <tr key={u.UNIT_ID}>
                <td>{u.UNIT_NAME}</td>
                <td>{u.PROPERTY_NAME}</td>
                <td>{u.ACCESS_CODE}</td>
                <td>{u.BEDS24_ID || "—"}</td>
                <td>
                  {u.ICAL_LINK
                    ? <a href={u.ICAL_LINK} target="_blank" rel="noopener noreferrer">View iCal</a>
                    : "—"
                  }
                </td>
                <td>
                  <button onClick={() => setNewUnit({
                    id: u.UNIT_ID,
                    name: u.UNIT_NAME,
                    access_code: u.ACCESS_CODE,
                    property_id: u.PROPERTY_ID,
                    beds24_id: u.BEDS24_ID,
                    ical_link: u.ICAL_LINK,
                  })}>Edit</button>
                  <button onClick={() => handleUnitDelete(u.UNIT_ID)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assignment Form */}
      <h2>User–Property Assignment</h2>
      <div className="assignment-form">
        <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)}>
          <option value="">Select User</option>
          {users.map(u => (
            <option key={u.USER_ID} value={u.USER_ID}>
              {u.USER_NAME} {u.USER_SURNAME}
            </option>
          ))}
        </select>

        <select value={assignPropertyId} onChange={e => setAssignPropertyId(e.target.value)}>
          <option value="">Select Property to Assign</option>
          {properties.map(p => (
            <option key={p.PROPERTY_ID} value={p.PROPERTY_ID}>
              {p.PROPERTY_NAME}
            </option>
          ))}
        </select>

        <button onClick={handleAssignProperty} disabled={!assignUserId || !assignPropertyId}>
          Assign Property
        </button>
      </div>

      {/* Assignments Table */}
      <h3>Current Assignments</h3>
      <div className="table-responsive">
        <table className="assignment-table">
          <thead>
            <tr><th>User</th><th>Property</th><th>Action</th></tr>
          </thead>
          <tbody>
            {assignments.map(a => (
              <tr key={`${a.USER_ID}-${a.PROPERTY_ID}`}>
                <td>
                  {users.find(u => u.USER_ID === a.USER_ID)?.USER_NAME}{" "}
                  {users.find(u => u.USER_ID === a.USER_ID)?.USER_SURNAME}
                </td>
                <td>{a.PROPERTY_NAME}</td>
                <td>
                  <button onClick={() => handleUnassignProperty(a.USER_ID, a.PROPERTY_ID)}>
                    Unassign
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
